import React from "react";
import {
  AppBar,
  Box,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink } from "react-router-dom";
import { siteMeta } from "../../data/mockData";
import { NavMenu } from "./index";
import refwaLogo from "../../assets/refwa_usa.png";

const Header = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const closeMobileMenu = () => setMobileOpen(false);

  return (
    <>
      <Box
        sx={{
          background:
            "linear-gradient(90deg, #08111f 0%, #0c1522 52%, #111b2b 100%)",
          color: "rgba(255,255,255,0.78)",
          py: 0.85,
          display: { xs: "none", lg: "block" },
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Container>
          <Stack
            direction="row"
            spacing={4}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack
              direction="row"
              spacing={1.15}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <PlaceOutlinedIcon
                sx={{ color: "primary.light", fontSize: 18 }}
              />
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                  color: "rgba(255,255,255,0.76)",
                }}
              >
                {siteMeta.address}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1.15}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <MailOutlineIcon sx={{ color: "primary.light", fontSize: 18 }} />
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                  color: "rgba(255,255,255,0.76)",
                }}
              >
                {siteMeta.email} | {siteMeta.officeHours}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{
          top: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(20px)",
          backgroundColor: "rgba(248, 244, 237, 0.92)",
          borderBottom: "1px solid rgba(19,33,51,0.09)",
          boxShadow: "0 14px 34px rgba(10, 18, 30, 0.055)",
        }}
      >
        <Container>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 72, md: 82 },
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              aria-label={`${siteMeta.name} home`}
              sx={{
                flexGrow: 1,
                display: "inline-flex",
                alignItems: "center",
                minWidth: 0,
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src={refwaLogo}
                alt={`${siteMeta.name} logo`}
                sx={{
                  width: { xs: 142, sm: 164, md: 188 },
                  height: { xs: 48, sm: 54, md: 60 },
                  objectFit: "contain",
                  objectPosition: "left center",
                  display: "block",
                  flexShrink: 0,
                  filter: "drop-shadow(0 10px 18px rgba(10, 18, 30, 0.12))",
                }}
              />
            </Box>

            <Box
              sx={{
                display: { xs: "none", lg: "block" },
                p: 0.65,
                borderRadius: 999,
                border: "1px solid rgba(19,33,51,0.08)",
                backgroundColor: "rgba(255,255,255,0.62)",
                boxShadow: "0 12px 28px rgba(10, 18, 30, 0.055)",
              }}
            >
              <NavMenu />
            </Box>

            <IconButton
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
              sx={{
                display: { xs: "inline-flex", lg: "none" },
                width: 44,
                height: 44,
                border: "1px solid rgba(19,33,51,0.12)",
                bgcolor: "rgba(255,255,255,0.72)",
                color: "primary.main",
                boxShadow: "0 10px 22px rgba(10, 18, 30, 0.06)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.92)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={closeMobileMenu}
        PaperProps={{
          sx: {
            width: { xs: "86vw", sm: 360 },
            maxWidth: 380,
            p: 0,
            background:
              "linear-gradient(180deg, #f8f4ed 0%, #f2ede5 58%, #ebe2d4 100%)",
            borderLeft: "1px solid rgba(19,33,51,0.08)",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              component={RouterLink}
              to="/"
              onClick={closeMobileMenu}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                minWidth: 0,
              }}
            >
              <Box
                component="img"
                src={refwaLogo}
                alt={`${siteMeta.name} logo`}
                sx={{
                  width: 158,
                  height: 54,
                  objectFit: "contain",
                  objectPosition: "left center",
                  display: "block",
                  filter: "drop-shadow(0 8px 14px rgba(10, 18, 30, 0.10))",
                }}
              />
            </Box>

            <IconButton
              onClick={closeMobileMenu}
              aria-label="Close navigation"
              sx={{
                width: 40,
                height: 40,
                border: "1px solid rgba(19,33,51,0.1)",
                bgcolor: "rgba(255,255,255,0.72)",
                color: "primary.main",
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Typography
            variant="overline"
            sx={{
              display: "block",
              mt: 2.5,
              color: "primary.main",
              fontWeight: 800,
              letterSpacing: "0.16em",
            }}
          >
            Navigation
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.75,
              color: "text.secondary",
              lineHeight: 1.65,
              maxWidth: 280,
            }}
          >
            Explore Refwa capabilities, services, portfolio, articles, and
            contact information.
          </Typography>

          <Divider sx={{ my: 2.5, borderColor: "rgba(19,33,51,0.1)" }} />

          <Box
            sx={{
              "& a": {
                width: "100%",
                justifyContent: "flex-start",
              },
            }}
          >
            <NavMenu onNavigate={closeMobileMenu} />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
